import React, { FC, PropsWithChildren, createElement } from 'react';
import { SvgXml } from 'react-native-svg';
import { View } from 'react-native';

import { GradientWithImage } from '../Cards/GradientWithImage';
import { heroXmlFor } from './heroes';
import { isWeb } from '../../config/designSystem/Layout';

interface HeroBackgroundActivityProps extends PropsWithChildren {
  /** module gradient key (figma variant: Module) */
  designSystemColor: string;
  /** activity type selects the illustrated scene (figma variant: Activity Type) */
  activityType?: string | null;
  /** custom illustration override */
  image?: string | number | null;
  width: number;
  height: number;
}

/**
 * figma: ❖ Hero Background Activity (Activities & Challenges pt1)
 * The activity header: the design's illustrated scene per (type, module),
 * embedded as SVG so it stays crisp at any width. Falls back to the module
 * gradient when no illustration exists for the combination.
 * react-native-svg does not ship SvgXml on web, so the web build renders the
 * SVG through a data-URI image instead.
 */
export const HeroBackgroundActivity: FC<HeroBackgroundActivityProps> = ({
  designSystemColor,
  activityType,
  image = null,
  width,
  height,
  children,
}) => {
  const xml = heroXmlFor(activityType, designSystemColor);

  if (xml) {
    // full-bleed, anchored to the illustration's base
    const responsiveXml = xml.replace(
      /<svg([^>]*)width="390" height="127"([^>]*)>/,
      '<svg$1width="100%" height="100%" preserveAspectRatio="xMidYMax slice"$2>'
    );
    return (
      <View style={{ width, height, overflow: 'hidden' }}>
        {isWeb
          // react-native-svg has no SvgXml on web: inline the markup (the
          // web renderer is react-dom, so a plain div works here)
          ? createElement('div', {
            dangerouslySetInnerHTML: { __html: responsiveXml },
            style: { width: '100%', height: '100%' },
          })
          : (
            <SvgXml
              xml={xml}
              width="100%"
              height="100%"
              viewBox="0 0 390 127"
              preserveAspectRatio="xMidYMax slice"
            />
          )}
        {children}
      </View>
    );
  }

  return (
    <GradientWithImage
      image={image}
      designSystemColor={designSystemColor}
      width={width}
      height={height}
      style={{ borderRadius: 0 }}
    >
      {children}
    </GradientWithImage>
  );
};
